import { useState } from "react";
import { Plus } from "lucide-react";
import { Badge, Button, Card, CardHeader, Input } from "@vseh/ui";

import { listingsApi, type ListingDto } from "../shared/api";
import { useFetch } from "../shared/hooks";
import { useT } from "../shared/i18n";
import { EmptyBlock, ErrorBlock } from "../shared/components/States";
import { SkeletonTable } from "../shared/components/Skeleton";
import { PhotoThumb } from "../shared/components/PhotoThumb";


export function ListingsPage(): JSX.Element {
  const t = useT();
  const [draftHint, setDraftHint] = useState("");
  const [draftUrl, setDraftUrl]   = useState("");
  const [posting, setPosting]     = useState(false);
  const [postError, setPostError] = useState<Error | null>(null);
  const [tick, setTick]           = useState(0);

  const { data, error, loading, refetch } = useFetch<ListingDto[]>(
    (signal) => listingsApi.list(50, 0, { signal }),
    [tick],
  );

  const postDraft = async () => {
    if (!draftUrl.trim() || posting) return;
    setPosting(true);
    setPostError(null);
    try {
      await listingsApi.postDraft([draftUrl.trim()], draftHint || undefined);
      setDraftUrl("");
      setDraftHint("");
      // Refresh after a short delay — agent needs ~1s.
      setTimeout(() => setTick((n) => n + 1), 1200);
    } catch (e) {
      setPostError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={t("listings.draft.title")}
          description={t("listings.draft.subtitle")}
        />
        <div className="flex flex-col gap-2">
          <Input
            label={t("listings.draft.url")}
            placeholder="https://example.com/photo.jpg"
            value={draftUrl}
            onChange={(e) => setDraftUrl(e.target.value)}
          />
          <Input
            label={t("listings.draft.hint")}
            placeholder={t("listings.draft.hint.placeholder")}
            value={draftHint}
            onChange={(e) => setDraftHint(e.target.value)}
          />
          {postError && <ErrorBlock error={postError} />}
          <div>
            <Button
              variant="ai"
              leftIcon={<Plus className="size-4" />}
              loading={posting}
              disabled={!draftUrl.trim()}
              onClick={postDraft}
            >
              {t("listings.draft.submit")}
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          title={t("listings.page.title")}
          description={data ? `${data.length}` : undefined}
          actions={<Button variant="ghost" size="xs" onClick={refetch}>{t("common.refresh")}</Button>}
        />

        {loading && !data && <SkeletonTable rows={6} cols={5} />}
        {error && <ErrorBlock error={error} onRetry={refetch} />}
        {data && data.length === 0 && (
          <EmptyBlock
            title={t("listings.empty.title")}
            description={t("listings.empty.body")}
          />
        )}

        {data && data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-base-200 text-left text-2xs uppercase tracking-wide text-base-500">
                  <th className="py-2 pr-3 font-medium"></th>
                  <th className="py-2 pr-3 font-medium">{t("listings.col.title")}</th>
                  <th className="py-2 pr-3 font-medium">{t("listings.col.type")}</th>
                  <th className="py-2 pr-3 font-medium">{t("listings.col.status")}</th>
                  <th className="py-2 pr-3 font-medium text-right">{t("listings.col.photos")}</th>
                  <th className="py-2 pr-3 font-medium text-right">{t("listings.col.views")}</th>
                  <th className="py-2 pr-3 font-medium">Origin</th>
                  <th className="py-2 pr-3 font-medium">{t("listings.col.created")}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((l) => (
                  <tr key={l.id} className="border-b border-base-100 hover:bg-base-50">
                    <td className="py-2 pr-3">
                      <PhotoThumb
                        photoId={l.cover_photo_id}
                        size={36}
                        rounded
                        alt={l.title}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <div className="font-medium text-base-900">{l.title}</div>
                      {l.description && (
                        <div className="mt-0.5 line-clamp-1 text-2xs text-base-500">
                          {l.description}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-base-700">
                      {String((l.specs as Record<string, unknown>).equipment_type ?? "—")}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge
                        tone={l.status === "published" ? "success" : "neutral"}
                        dot
                      >
                        {l.status}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{l.photo_count}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{l.view_count}</td>
                    <td className="py-2 pr-3 text-2xs text-base-500">{l.provenance}</td>
                    <td className="py-2 pr-3 text-2xs text-base-500">
                      {new Date(l.created_at).toLocaleString("ru-RU")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
